import type { DocPageContent } from '../docs-content'

const page = {
  order: 20,
  slug: 'keyboard-shortcuts',
  title: 'Accesos Directos del Teclado',
  description: 'Accesos directos predeterminados por grupo, además de cómo buscar, reasignar y restablecerlos.',
  category: 'Referencia',
  sections: [
    {
      title: 'Reproducción',
      blocks: [
        {
          kind: 'table',
          headers: ['Acción', 'Atajo'],
          rows: [
            ['Reproducir / Pausar', '`Espacio`'],
            ['Fotograma anterior / siguiente', '`Izquierda` / `Derecha`'],
            ['Punto de ajuste anterior / siguiente', '`Arriba` / `Abajo`'],
            ['Ir al inicio / final', '`Inicio` / `Fin`'],
          ],
        },
      ],
    },
    {
      title: 'Edición',
      blocks: [
        {
          kind: 'table',
          headers: ['Acción', 'Atajo'],
          rows: [
            ['Dividir en el cabezal de reproducción', '`Ctrl+K` (también `Alt+C`)'],
            ['Dividir en el cursor', '`Mayús+C`'],
            ['Unir', '`Mayús+J`'],
            ['Eliminar / Eliminar con ripple', '`Suprimir` / `Ctrl+Suprimir`'],
            ['Insertar fotograma congelado', '`Mayús+F`'],
            ['Vincular / Desvincular clips', '`Ctrl+Alt+L` / `Alt+Mayús+L`'],
            ['Alternar selección vinculada', '`Mayús+L`'],
            ['Desplazar 1px / 10px', '`Mayús+Flecha` / `Ctrl+Mayús+Flecha`'],
          ],
        },
      ],
    },
    {
      title: 'Herramientas',
      blocks: [
        {
          kind: 'table',
          headers: ['Herramienta', 'Atajo'],
          rows: [
            ['Selección', '`V`'],
            ['Editar recorte', '`T`'],
            ['Cuchilla', '`C`'],
            ['Estiramiento de velocidad', '`R`'],
            ['Deslizamiento interno', '`Y`'],
            ['Deslizamiento externo', '`U`'],
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          text: 'El ripple y el rolling son comportamientos de recorte de la herramienta **Editar recorte**, no herramientas separadas con su propio atajo.',
        },
      ],
    },
    {
      title: 'Historial, vista y portapapeles',
      blocks: [
        {
          kind: 'table',
          headers: ['Acción', 'Atajo'],
          rows: [
            ['Deshacer / Rehacer', '`Ctrl+Z` / `Ctrl+Mayús+Z`'],
            ['Copiar / Cortar / Pegar', '`Ctrl+C` / `Ctrl+X` / `Ctrl+V`'],
            ['Alternar ajuste / ajuste del lienzo', '`S` / `Mayús+S`'],
            ['Acercar / Alejar', '`Ctrl+=` / `Ctrl+-`'],
            ['Ajustar al tamaño / 100%', '`\\` / `Mayús+\\`'],
            ['Alternar editor de fotogramas clave', '`Ctrl+Mayús+A`'],
            ['Espacio de trabajo Editar / Color', '`Alt+1` / `Alt+2`'],
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          text: 'El espacio de trabajo Animar no tiene un atajo predeterminado — ábrelo desde su pestaña en la barra de herramientas.',
        },
      ],
    },
    {
      title: 'Marcadores y fotogramas clave',
      blocks: [
        {
          kind: 'table',
          headers: ['Acción', 'Atajo'],
          rows: [
            ['Añadir / Eliminar marcador', '`M` / `Mayús+M`'],
            ['Marcador anterior / siguiente', '`[` / `]`'],
            ['Limpiar fotogramas clave', '`Mayús+A`'],
            ['Vista gráfico / hoja de fotogramas clave', '`1` / `2`'],
          ],
        },
      ],
    },
    {
      title: 'Monitor de origen y proyecto',
      blocks: [
        {
          kind: 'table',
          headers: ['Acción', 'Atajo'],
          rows: [
            ['Marcar Entrada / Salida', '`I` / `O`'],
            ['Limpiar Entrada/Salida', '`Alt+X`'],
            ['Edición de insertar / sobrescribir', '`,` / `.`'],
            ['Guardar', '`Ctrl+S`'],
            ['Exportar', '`Ctrl+Mayús+E`'],
            ['Abrir Navegador de Escenas', '`Ctrl+Mayús+F`'],
          ],
        },
      ],
    },
    {
      title: 'Personalizar los accesos directos',
      blocks: [
        {
          kind: 'paragraph',
          text: 'Cada uno de los atajos anteriores es un valor predeterminado preinstalado. Abre el editor de accesos directos rediseñado desde el botón **Accesos Directos del Teclado** (icono de teclado) en la barra de herramientas del editor para reasignar cualquier comando a las teclas que prefieras.',
        },
        {
          kind: 'steps',
          items: [
            'Busca comandos por nombre, o filtra por los grupos **Personalizado**, **Conflictos** o **Sin asignar**.',
            'Selecciona un comando y pulsa **Grabar**, luego mantén pulsadas las teclas modificadoras y pulsa la tecla final.',
            'Si la combinación ya está en uso, el editor señala el conflicto — elige **Sobrescribir** para reasignarla, o selecciona otras teclas.',
            'Usa **Desvincular** para eliminar una asignación, o añade una asignación alternativa junto a la principal.',
          ],
        },
        {
          kind: 'note',
          tone: 'tip',
          text: 'Usa **Restablecer** para restaurar un solo comando, o **Restablecer todo** para devolver cada atajo a su valor predeterminado. También puedes **Exportar** tus asignaciones a un archivo de preajuste e **Importarlo** en otra máquina.',
        },
      ],
    },
  ],
} satisfies DocPageContent

export default page